import React from "react";
import {
    ChevronUp,
    ChevronDown,
    ChevronsLeft,
    ChevronsRight,
    ChevronLeft,
    ChevronRight,
} from "lucide-react";
import { Skeleton } from "components/ui/skeleton";
import { useSmoothLoading } from "lib/use-smooth-loading";
import type { RNSRow } from "../api/index";

interface Props {
    data: RNSRow[];
    loading: boolean;
    page: number;
    pageSize: number;
    totalItems: number;
    sortBy: string;
    sortOrder: "asc" | "desc";
    onSortChange: (col: keyof RNSRow) => void;
    onPageChange: (page: number) => void;
    onPageSizeChange: (size: number) => void;
    hasLoadedOnce: boolean;
    onRowClick: (customerCode: string, customerName: string) => void;
}

const columns: Array<{ key: keyof RNSRow; label: string; isNumeric?: boolean }> = [
    { key: "customer_name", label: "Customer" },
    { key: "rns_count", label: "RNS Count", isNumeric: true },
    { key: "in_transit", label: "In Transit", isNumeric: true },
    { key: "in_stock", label: "In Stock", isNumeric: true },
    { key: "delivered", label: "Delivered", isNumeric: true },
    { key: "rns_lte_15_days", label: "RNS <= 15 Days", isNumeric: true },
    { key: "rns_gt_15_days", label: "RNS > 15 Days", isNumeric: true },
];

const RNSTable: React.FC<Props> = ({
    data,
    loading,
    page,
    pageSize,
    totalItems,
    sortBy,
    sortOrder,
    onSortChange,
    onPageChange,
    onPageSizeChange,
    hasLoadedOnce,
    onRowClick,
}) => {
    const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));

    const { isLoading } = useSmoothLoading(loading, {
        minLoadingTime: 150,
        debounceTime: 150,
    });

    const renderSkeletonRows = () => {
        return Array.from({ length: 10 }).map((_, i) => (
            <tr key={i}>
                {columns.map((_, j) => (
                    <td key={j} className="border border-gray-200 px-2 py-1.5">
                        <Skeleton className="h-4 w-full" />
                    </td>
                ))}
            </tr>
        ));
    };

    return (
        <div className="flex flex-col h-full">
            <div className="border border-gray-200 overflow-auto flex-1">
                <table className="w-full border-collapse text-sm">
                    <thead>
                        <tr className="bg-gray-100">
                            {columns.map((col) => {
                                const active = sortBy === col.key;

                                return (
                                    <th
                                        key={col.key}
                                        onClick={() => onSortChange(col.key)}
                                        className={`border border-gray-300 px-2 py-2 cursor-pointer whitespace-nowrap font-semibold
                                            ${col.isNumeric ? "text-right" : "text-left"}`}
                                    >
                                        <div className={`flex items-center gap-1 ${col.isNumeric ? "justify-end" : "justify-start"}`}>
                                            {col.label}
                                            {active ? (sortOrder === "asc" ? (<ChevronUp size={14} />) : (<ChevronDown size={14} /> )) 
                                            : (<ChevronUp size={14} className="text-gray-300" />)}
                                        </div>
                                    </th>
                                );
                            })}
                        </tr>
                    </thead>

                    <tbody>
                        {isLoading || !hasLoadedOnce ? (
                            renderSkeletonRows()
                        ) : data.length === 0 ? (
                            <tr>
                                <td colSpan={columns.length} className="text-center py-16 text-gray-500">
                                    No data available
                                </td>
                            </tr>
                        ) : (
                            data.map((row, idx) => {
                                const isTotalRow = row.customer_code === "TOTAL" || row.customer_name === "TOTAL";

                                return (
                                    <tr
                                        key={`${row.customer_code}-${idx}`}
                                        onClick={isTotalRow ? undefined : () => onRowClick(row.customer_code, row.customer_name)}
                                        className={`hover:bg-gray-50 ${isTotalRow ? "bg-blue-50 font-semibold" : "cursor-pointer"}`}
                                    >
                                        {columns.map((col) => (
                                            <td
                                                key={col.key}
                                                className={`border border-gray-200 px-2 py-1.5 whitespace-nowrap ${
                                                    col.isNumeric ? "text-right" : "text-left"
                                                }`}
                                            >
                                                {col.isNumeric
                                                    ? Number(row[col.key] || 0).toLocaleString("en-IN")
                                                    : row[col.key]}
                                            </td>
                                        ))}
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </table>
            </div>

            <div className="flex items-center justify-between pt-4">
                <span className="text-sm text-gray-600">
                    Showing{" "}
                    <strong>
                        {totalItems === 0 ? 0 : (page - 1) * pageSize + 1}
                    </strong>{" "}
                    to{" "}
                    <strong>{Math.min(page * pageSize, totalItems)}</strong> of{" "}
                    <strong>{totalItems}</strong>
                </span>

                <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2 text-sm">
                        Rows per page
                        <select
                            value={pageSize}
                            onChange={(e) => onPageSizeChange(Number(e.target.value))}
                            className="border rounded px-2 py-1"
                        >
                            {[10, 20, 50, 100].map((s) => (
                                <option key={s} value={s}>
                                    {s}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="flex items-center gap-1">
                        <button
                            disabled={page === 1}
                            onClick={() => onPageChange(1)}
                            className="p-1 border rounded disabled:opacity-50"
                        >
                            <ChevronsLeft size={16} />
                        </button>

                        <button
                            disabled={page === 1}
                            onClick={() => onPageChange(page - 1)}
                            className="p-1 border rounded disabled:opacity-50"
                        >
                            <ChevronLeft size={16} />
                        </button>

                        <span className="px-2 text-sm">
                            Page <strong>{page}</strong> / {totalPages}
                        </span>

                        <button
                            disabled={page === totalPages}
                            onClick={() => onPageChange(page + 1)}
                            className="p-1 border rounded disabled:opacity-50"
                        >
                            <ChevronRight size={16} />
                        </button>

                        <button
                            disabled={page === totalPages}
                            onClick={() => onPageChange(totalPages)}
                            className="p-1 border rounded disabled:opacity-50"
                        >
                            <ChevronsRight size={16} />
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default RNSTable;
